import { composeRenderProps } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string | Array<string | false | undefined>,
): string | ((v: T) => string) {
  return composeRenderProps(className, (className) => twMerge(tw, className));
}

export const focusOutlineStyle = [
  'outline outline-2 outline-emerald-600 outline-offset-2',
  'group-data-[readonly]:outline-emerald-400',
];

export const focusVisibleOutlineStyle = [
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 focus-visible:outline-offset-2',
  'group-readonly:focus-visible:outline-emerald-400',
];

export const focusRingStyle = [
  'ring-1 ring-emerald-600 border-emerald-600 ring-inset',
  'invalid:border-emerald-600 group-invalid:border-emerald-600',

  // Readonly style
  '[&[readonly]]:ring-emerald-400 [&[readonly]]:border-emerald-400',
  '[&:has(:is([readonly],[aria-readonly]))]:ring-emerald-400 [&:has(:is([readonly],[aria-readonly]))]:border-emerald-400',
];

export const focusedRingStyle = [
  'focus:ring-1 focus:ring-emerald-600 focus:border-emerald-600 focus:ring-inset',
  'invalid:focus:border-emerald-600 group-invalid:focus:border-emerald-600',

  // focus:Readonly focus:style
  'focus:[&[readonly]]:ring-emerald-400 focus:[&[readonly]]:border-emerald-400',
  '[&:has(:is([readonly],[aria-readonly]))]:focus:ring-emerald-400 [&:has(:is([readonly],[aria-readonly]))]:focus:border-emerald-400',
];

export const focusWithinRingStyle = [
  'focus-within:ring-1 focus-within:ring-emerald-600 focus-within:border-emerald-600 focus-within:ring-inset',
  'invalid:focus-within:border-emerald-600 group-invalid:focus-within:border-emerald-600',

  // focus-within:Readonly focus-within:style
  'focus-within:[&[readonly]]:ring-emerald-400 focus-within:[&[readonly]]:border-emerald-400',
  '[&:has(:is([readonly],[aria-readonly]))]:focus-within:ring-emerald-400 [&:has(:is([readonly],[aria-readonly]))]:focus-within:border-emerald-400',
];

// RAC uses `slot=*`. We use `data-ui=* to avoid potential conflict
export const inputFieldStyle = [
  'group',
  // Label style
  // '[&_[data-ui=label]:not([class*=mb-])]:mb-1',
  // '[&_[data-ui=label]:not([class*=mb-]):has(+:is(input,textarea,[data-ui=control]))]:mb-2',

  // Description style
  '[&>:is(input,[data-ui=control])+[data-ui=description]:not([class*=mt-])]:mt-2',
  '[&>textarea+[data-ui=description]:not([class*=mt-])]:mt-0.5',
  '[&_[data-ui=description]:not([class*=mb-]):has(+:is(input,textarea,[data-ui=control]))]:mb-3',

  // Error
  // RAC FieldError does not accept data-ui
  '[&>:is(input,textarea,[data-ui=control])+[slot=errorMessage]:not([class*=mt-])]:mt-2',
  '[&:has([data-ui=description]+[slot=errorMessage])_[slot=errorMessage]]:mt-1',
];

// We separate out the "base color" design of the form so we can use it on other components that should retain the color
export const inputInputBaseStyle = ['border bg-solidQuiet shadow-sm outline-none', 'rounded'];

export const groupBoxStyle = [
  'group flex flex-col',

  // Group description style
  '[&_[data-ui=description]:not([class*=mt-]):has(+[data-ui=box])]:mt-1',
  '[&_[data-ui=description]:not([class*=mt-]):has(+[data-ui=box])]:mb-4',

  // Group box style
  '[&:not(:has([data-ui=description]+[data-ui=box]))>[data-ui=box]]:mt-3',

  '[&:has(:is([type=checkbox],[type=radio],[role=switch]))_[data-ui=box]:not([class*=gap-])]:gap-y-3',

  //Group label style when field has description
  '[&:has([data-ui=field]>[data-ui=description])>[data-ui=label]]:font-semibold',

  // Box item description inside
  '[&:has(:is([type=checkbox],[type=radio],[role=switch]))_[data-ui=box]:has([data-ui=description]):not([class*=gap-y])]:gap-y-4',

  // Horizontal
  '[&[data-orientation=horizontal]:has(:is([type=checkbox],[type=radio],[role=switch]))_[data-ui=box]:not([class*=gap-x-])]:gap-x-4',
  '[&[data-orientation=horizontal]:has(:is([type=checkbox],[type=radio],[role=switch]))_[data-ui=box]:not([class*=gap-y-])]:gap-y-2',

  // Error
  '[&:has([data-ui=box]+[slot=errorMessage])_[slot=errorMessage]]:mt-2',
];

// Chevron down as select box indicator
export const selectBoxIndicator = [
  'after:pointer-events-none',
  'after:absolute',
  'after:border-muted',
  "after:content-['']",
  'after:size-[7px] after:sm:size-[6px]',
  'after:border-b-[1.5px] after:border-r-[1.5px]',
  'after:bottom-[55%] after:end-3 after:-translate-x-1/2 after:translate-y-1/2 after:rotate-45 rtl:after:translate-x-1.5',
];

export const displayLevels = {
  1: 'font-medium text-2xl',
  2: 'font-medium text-base/6',
  3: 'font-medium text-base/6 sm:text-sm/6',
};

export type DisplayLevel = keyof typeof displayLevels;
